/**
 * @file DesignSourceList.tsx
 * @description «Какого» в окне «Создать дизайн» (решения 2026-10-12):
 * радио-строки 56px с образцом цветов, названием и отметкой «на сайте»;
 * до 20 строк, прокрутка не выше 280px. Под списком — откуда возьмутся
 * логотипы (как у источника).
 */

import type { SiteDesignItem } from "../../../lib/api-site-designs";
import { cn } from "@/lib/utils";
import { DesignColorDots } from "./DesignSwatches";

interface Props {
  items: readonly SiteDesignItem[];
  value: string | null;
  onChange: (id: string) => void;
}

export function DesignSourceList({ items, value, onChange }: Props) {
  const selected = items.find((d) => d.id === value) ?? null;
  return (
    <div className="flex flex-col gap-2">
      <span id="design-source-label" className="text-sm font-medium text-on-surface">
        Какого
      </span>
      <div
        role="radiogroup"
        aria-labelledby="design-source-label"
        className="flex max-h-[280px] flex-col gap-1 overflow-y-auto rounded-xl p-1 [box-shadow:inset_0_0_0_1px_var(--border)]"
      >
        {items.map((item) => {
          const active = item.id === value;
          return (
            <button
              key={item.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(item.id)}
              className={cn(
                "flex min-h-14 shrink-0 items-center gap-3 rounded-lg px-3 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                active ? "bg-primary/8 [box-shadow:inset_0_0_0_2px_var(--primary)]" : "hover:bg-surface-low",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "flex size-5 shrink-0 items-center justify-center rounded-full [box-shadow:inset_0_0_0_2px_var(--muted-foreground)]",
                  active && "[box-shadow:inset_0_0_0_2px_var(--primary)]",
                )}
              >
                {active ? <span className="size-2.5 rounded-full bg-primary" /> : null}
              </span>
              <DesignColorDots summary={item.summary} size="sm" />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="type-body truncate text-on-surface" title={item.name}>
                  {item.name}
                </span>
                {item.matchesPublished ? <span className="type-caption text-on-surface-variant">на сайте</span> : null}
              </span>
            </button>
          );
        })}
      </div>
      {selected ? (
        <span className="type-caption text-on-surface-variant">
          {selected.summary.withLogos ? `Логотипы — как в «${selected.name}»` : "Логотипы — как у сайта сейчас"}
        </span>
      ) : null}
    </div>
  );
}
